import styled from 'styled-components'
import { Input } from 'antd'
import { SPACING, TYPOGRAPHY, FONT_WEIGHT } from '../../../../styles/theme'

// Shared loading styles
export const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 300px;
  gap: ${SPACING.lg};
`

export const LoadingIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`

export const LoadingMessage = styled.div`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  color: var(--color-text-secondary);
  text-align: center;
  max-width: 300px;
`

// Shared modal content styles
export const ModalContentWrapper = styled.div`
  margin-bottom: ${SPACING.lg};
`

export const ModalSecondaryText = styled.span`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  color: var(--color-text-secondary);
`

// Prompt export styles
export const PromptTextArea = styled(Input.TextArea)`
  font-family: monospace;
  font-size: ${TYPOGRAPHY.caption.fontSize};
  white-space: pre-wrap;
  word-break: break-word;
`

// ============================================================================
// VIEW MODE - CAROUSEL SINGLE PANEL DISPLAY
// ============================================================================

// Carousel container for single panel view with navigation
export const CarouselViewContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${SPACING.lg};
  width: 100%;
  padding: 4px 0 8px;

  @media (max-width: 640px) {
    gap: ${SPACING.sm};
    padding: 2px 0 4px;
  }
`

// Navigation row — centers the panel
export const CarouselNavRow = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
`

// Arrow button for carousel navigation — always overlaid on the panel
export const CarouselArrowButton = styled.button<{ $hidden?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  z-index: 10;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.45);
  border: 2px solid rgba(255, 255, 255, 0.35);
  color: #fff;
  cursor: pointer;
  visibility: ${(props) => (props.$hidden ? 'hidden' : 'visible')};
  transition: all 0.15s ease;

  &:first-of-type {
    left: 10px;
  }

  &:last-of-type {
    right: 10px;
  }

  &:hover:not(:disabled) {
    background: rgba(0, 0, 0, 0.7);
    border-color: rgba(255, 255, 255, 0.7);
  }

  &:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }

  @media (max-width: 640px) {
    width: 36px;
    height: 36px;
  }
`

// Single comic panel for carousel view - large for presentations
export const CarouselPanel = styled.div`
  background: var(--color-bg-primary);
  border: 3px solid #000;
  border-radius: 8px;
  overflow: hidden;
  max-width: 720px;
  width: 100%;
`

// Wraps the image and anchors the nav arrows to the image area
export const CarouselImageArea = styled.div`
  position: relative;
`

// Larger panel image for carousel
export const CarouselPanelImage = styled.img`
  width: 100%;
  aspect-ratio: 3 / 2;
  object-fit: cover;
  display: block;
`

// Placeholder shown in carousel when an image URL is broken
export const CarouselPanelPlaceholder = styled.div`
  width: 100%;
  aspect-ratio: 3 / 2;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-bg-tertiary);
  color: var(--color-text-tertiary);
  font-size: ${TYPOGRAPHY.caption.fontSize};
  gap: 8px;
`

// Caption box for carousel - taller with more room for text
export const CarouselCaptionBox = styled.div`
  padding: ${SPACING.lg} ${SPACING.xl};
  background: #ffffff;
  border-top: 1px solid rgba(0, 0, 0, 0.12);
  min-height: 120px;

  @media (max-width: 640px) {
    padding: 10px 14px;
    min-height: unset;
  }
`

// Larger panel title for carousel
export const CarouselPanelTitle = styled.div`
  font-size: ${TYPOGRAPHY.caption.fontSize};
  font-weight: ${FONT_WEIGHT.semibold};
  color: #666;
  margin-bottom: 8px;

  @media (max-width: 640px) {
    font-size: ${TYPOGRAPHY.caption.fontSize};
    margin-bottom: 5px;
  }
`

// Larger caption text for carousel - full text visible
export const CarouselCaptionText = styled.p`
  font-size: ${TYPOGRAPHY.body.fontSize};
  color: #1a1a1a;
  line-height: 1.65;
  margin: 0;
  font-family:
    system-ui,
    -apple-system,
    sans-serif;

  @media (max-width: 640px) {
    font-size: ${TYPOGRAPHY.caption.fontSize};
    line-height: 1.5;
  }
`

// Panel number badge for carousel
export const CarouselPanelNumber = styled.div`
  position: absolute;
  top: 12px;
  left: 12px;
  width: 36px;
  height: 36px;
  background: #000;
  color: #fff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: ${FONT_WEIGHT.bold};
  font-size: ${TYPOGRAPHY.body.fontSize};
  z-index: 2;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
`

// Dots indicator for current panel
export const CarouselDotsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${SPACING.sm};
`

export const CarouselDot = styled.button<{ $active?: boolean }>`
  width: ${(props) => (props.$active ? '24px' : '10px')};
  height: 10px;
  border-radius: 5px;
  background: ${(props) => (props.$active ? 'var(--color-primary)' : 'var(--color-border-dark)')};
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 0;

  &:hover {
    background: ${(props) =>
      props.$active ? 'var(--color-primary)' : 'var(--color-text-secondary)'};
  }
`

// ============================================================================
// EDIT MODE - PAGINATED FRAME EDITOR STYLES
// ============================================================================

// ============================================================================
// MULTI-SCENE EDITING STYLES
// ============================================================================

// ============================================================================
// ADVANCED MODE TOGGLE STYLES (for simplified storyboard editing)
// ============================================================================

// ============================================================================
// VERTICAL STORYBOARD EDITOR - ALL 4 FRAMES DISPLAYED AT ONCE
// ============================================================================
